import { Badge } from '@/components/ui/badge'
import { MatchBandBadge } from '@/components/evaluation/match-band-badge'
import { cn } from '@/lib/utils'
import type { CandidateMatchBand } from '@/lib/candidate-match-bands'
import { matchBand, normalizeMatchBand, type MatchBandInput } from '@/lib/match-bands'

export type MatchQualityBadgeProps = {
  /** Prefer API band when present; otherwise derived from `score`. */
  band?: MatchBandInput | CandidateMatchBand
  /** Score 0–1. Used to derive band when `band` is omitted, and for the secondary % chip. */
  score?: number | null
  /** Dashed secondary % chip (estimate / non-LLM provenance). */
  estimate?: boolean
  /** Show secondary percent chip when `score` is set. Default true. */
  showPercent?: boolean
  /** Text after the percent (default `"match"`; pass `""` for bare `87%`). */
  percentSuffix?: string
  /** Caption when band cannot be resolved. */
  caption?: string | null
  className?: string
}

function formatPercent(score: number, estimate: boolean, suffix: string): string {
  const pct = `${estimate ? '~' : ''}${Math.round(score * 100)}%`
  const trimmed = suffix.trim()
  return trimmed ? `${pct} ${trimmed}` : pct
}

/** Match quality: band chip primary, optional % secondary. */
export function MatchQualityBadge({
  band,
  score,
  estimate = false,
  showPercent = true,
  percentSuffix = 'match',
  caption,
  className,
}: MatchQualityBadgeProps) {
  const resolvedBand =
    band !== undefined
      ? normalizeMatchBand(band)
      : score != null
        ? matchBand(score)
        : null

  const bandChip =
    resolvedBand != null || caption ? (
      <MatchBandBadge band={resolvedBand} caption={caption} />
    ) : null

  const percentChip =
    showPercent && score != null ? (
      <Badge
        variant="outline"
        className={cn(
          'text-xs font-normal tabular-nums',
          estimate && 'border-dashed bg-transparent',
        )}
      >
        {formatPercent(score, estimate, percentSuffix)}
      </Badge>
    ) : null

  if (!bandChip && !percentChip) return null

  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1.5', className)}>
      {bandChip}
      {percentChip}
    </span>
  )
}
